import React from 'react';
import { useQuery } from '@apollo/client';
import { GET_PREVIOUS_RESERVATION } from '../../graphql/queries';
import { GET_ACTIVE_FLAG_TEMPLATES } from '../../graphql/flagTemplates';
import { renderReservationBadgesContainer } from '../../utils/job-badge-utils';
import SafeAvatar from '../SafeAvatar';

export default function JobMeta({ 
  job,
  nextReservation,
  nextReservationLoading = false,
  formatDateForDisplay,
  formatTimeOnly,
  convert24HourTo12Hour,
  getServiceTypeDisplayName,
  isPublicView = false
}) {
  // Fetch previous reservation for inspection jobs
  const shouldFetchPreviousReservation = job?.serviceType?.timeBase === 'CHECKIN' && job?.property?.id && job?.scheduledDate;
  
  const { data: previousReservationData, loading: previousReservationLoading } = useQuery(GET_PREVIOUS_RESERVATION, {
    variables: {
      propertyId: job?.property?.id,
      date: job?.scheduledDate ? new Date(job.scheduledDate).toISOString() : undefined
    },
    skip: !shouldFetchPreviousReservation,
    fetchPolicy: 'cache-and-network'
  });

  const previousReservation = previousReservationData?.previousReservationForProperty;

  // Fetch flag templates to pass to renderReservationBadgesContainer for custom flags
  const { data: templatesData } = useQuery(GET_ACTIVE_FLAG_TEMPLATES, {
    skip: !job?.id
  });
  const flagTemplates = templatesData?.getActiveFlagTemplates || [];

  // Helper to format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Helper to check if payout should be displayed
  const hasPayoutValue = () => {
    // Show payout if:
    // 1. Job has a custom payout value set, OR
    // 2. Service type title is "Cleaning", OR
    // 3. Service type has usePropertyFinancialFields enabled
    return (
      (job.payout !== null && job.payout !== undefined) ||
      job.serviceType?.title === 'Cleaning' ||
      job.serviceType?.usePropertyFinancialFields === true
    );
  };

  // Determine the actual payout value from all possible sources
  const getPayoutValue = () => {
    // Priority 1: Job's custom payout (overrides everything)
    if (job.payout !== null && job.payout !== undefined) {
      return formatCurrency(job.payout);
    }
    
    // Priority 2: Property's default payout (if service type is "Cleaning" or has usePropertyFinancialFields enabled)
    if (job.serviceType?.title === 'Cleaning' || job.serviceType?.usePropertyFinancialFields === true) {
      if (job.property?.payout !== null && job.property?.payout !== undefined) {
        return formatCurrency(job.property.payout);
      } else if (job.property?.payoutAmount !== null && job.property?.payoutAmount !== undefined) {
        return formatCurrency(job.property.payoutAmount);
      }
    }
    
    return null;
  };

  
  // Only show payout if job has custom payout OR service type is "Cleaning" OR service type uses property financial fields
  const canShowPayout = hasPayoutValue();


  // Calculate the number of columns to display
  const calculateGridCols = () => {
    if (isPublicView) return '2';
    if (!isPublicView && canShowPayout) return '3';
    return '2';
  };

  // Helper to get the appropriate start time based on service type
  const getStartTime = () => {
    // PRIORITY 1: Check if job has a custom start time set (should override everything else)
    if (job.startTime) {
      return {
        time: convert24HourTo12Hour(job.startTime),
        label: '(Custom time)'
      };
    }

    if (job.serviceType?.timeBase === 'CHECKOUT') {
      // Cleaning jobs - use current reservation's checkout time
      // Check override first, then specific time fields, then datetime fields, then property defaults
      
      if (job.reservation?.override_check_out) {
        // Extract time from ISO string using the same pattern as other components
        try {
          const isoString = job.reservation.override_check_out;
          const matches = isoString.match(/T(\d{2}):(\d{2}):(\d{2})/);
          
          if (matches && matches.length >= 3) {
            // Extract hours and minutes directly from ISO string
            const hours = matches[1];
            const minutes = matches[2];
            const timeResult = `${hours}:${minutes}`;
            return {
              time: convert24HourTo12Hour(timeResult),
              label: '(Guest checkout - override)'
            };
          } else {
            // Fallback to formatTimeOnly if regex fails
            const timeResult = formatTimeOnly(job.reservation.override_check_out);
            return {
              time: timeResult,
              label: '(Guest checkout - override)'
            };
          }
        } catch (error) {
          // Continue to next fallback option
        }
      } 
      
      if (job.reservation?.check_out_time) {
        return {
          time: convert24HourTo12Hour(job.reservation.check_out_time),
          label: '(Guest checkout)'
        };
      } 
      
      if (job.property?.checkOutTime) {
        return {
          time: convert24HourTo12Hour(job.property.checkOutTime),
          label: '(Default checkout)'
        };
      } 
      
      if (job.reservation?.check_out) {
        return {
          time: formatTimeOnly(job.reservation.check_out),
          label: '(Guest checkout)'
        };
      }
      return {
        time: '10:00 AM',
        label: '(Default checkout)'
      };
    } else if (job.serviceType?.timeBase === 'CHECKIN') {
      // Inspection jobs - use PREVIOUS reservation's checkout time
      if (previousReservationLoading) {
        return {
          time: 'Loading...',
          label: ''
        };
      }
      
      if (previousReservation) {
        if (previousReservation.override_check_out) {
          return {
            time: formatTimeOnly(previousReservation.override_check_out),
            label: '(Previous checkout)'
          };
        } else if (previousReservation.check_out_time) {
          return {
            time: convert24HourTo12Hour(previousReservation.check_out_time),
            label: '(Previous checkout)'
          };
        } else if (previousReservation.property?.checkOutTime) {
          return {
            time: convert24HourTo12Hour(previousReservation.property.checkOutTime),
            label: '(Previous checkout)'
          };
        } else if (previousReservation.check_out) {
          return {
            time: formatTimeOnly(previousReservation.check_out),
            label: '(Previous checkout)'
          };
        }
      }
      
      // Fallback for inspection jobs when no previous reservation
      return {
        time: '10:00 AM',
        label: '(No previous checkout)'
      };
    } else if (job.serviceType?.defaultTime) {
      // Custom time jobs
      return {
        time: convert24HourTo12Hour(job.serviceType.defaultTime),
        label: '(Service type default)'
      };
    } else {
      // Fallback
      if (job.property?.checkOutTime) {
        return {
          time: convert24HourTo12Hour(job.property.checkOutTime),
          label: '(Default time)'
        };
      } else if (job.reservation?.check_out) {
        return {
          time: formatTimeOnly(job.reservation.check_out),
          label: '(Default time)'
        };
      }
      return {
        time: '10:00 AM',
        label: '(Default time)'
      };
    }
  };

  // Helper to get the appropriate due time - matches DueByDisplay logic exactly
  const getDueTime = () => {
    if (job.serviceType?.timeBase === 'CHECKIN') {
      // For inspection jobs, due time is current reservation's check-in (the actual check-in date, not job scheduled date)
      if (job.reservation?.override_check_in) {
        // Extract time from ISO string using the same pattern as other components
        try {
          const isoString = job.reservation.override_check_in;
          const matches = isoString.match(/T(\d{2}):(\d{2}):(\d{2})/);
          
          if (matches && matches.length >= 3) {
            // Extract hours and minutes directly from ISO string
            const hours = matches[1];
            const minutes = matches[2];
            const timeResult = `${hours}:${minutes}`;
            return {
              date: formatDateForDisplay(job.reservation?.check_in || job.scheduledDate), // Use reservation check_in date, not scheduled date
              time: convert24HourTo12Hour(timeResult),
              label: '(Check-in - override)'
            };
          } else {
            // Fallback to formatTimeOnly if regex fails
            const timeResult = formatTimeOnly(job.reservation.override_check_in);
            return {
              date: formatDateForDisplay(job.reservation?.check_in || job.scheduledDate), // Use reservation check_in date, not scheduled date
              time: timeResult,
              label: '(Check-in - override)'
            };
          }
        } catch (error) {
          return {
            date: formatDateForDisplay(job.reservation?.check_in || job.scheduledDate), // Use reservation check_in date, not scheduled date
            time: formatTimeOnly(job.reservation.override_check_in),
            label: '(Check-in - override)'
          };
        }
      } else if (job.reservation?.check_in_time) {
        return {
          date: formatDateForDisplay(job.reservation?.check_in || job.scheduledDate), // Use reservation check_in date, not scheduled date
          time: convert24HourTo12Hour(job.reservation.check_in_time),
          label: '(Check-in)'
        };
      } else if (job.property?.checkInTime) {
        return {
          date: formatDateForDisplay(job.reservation?.check_in || job.scheduledDate), // Use reservation check_in date, not scheduled date
          time: convert24HourTo12Hour(job.property.checkInTime),
          label: '(Check-in)'
        };
      } else if (job.reservation?.check_in) {
        return {
          date: formatDateForDisplay(job.reservation.check_in), // Use reservation check_in date, not scheduled date
          time: formatTimeOnly(job.reservation.check_in),
          label: '(Check-in)'
        };
      } else {
        return {
          date: formatDateForDisplay(job.reservation?.check_in || job.scheduledDate), // Fallback to scheduled date if no check_in
          time: '4:00 PM',
          label: '(Default check-in)'
        };
      }
    } else {
      // For cleaning jobs, due time is next reservation's check-in
      if (nextReservationLoading) {
        return {
          date: 'Loading...',
          time: '',
          label: ''
        };
      }
      
      if (!nextReservation) {
        return {
          date: 'No next check-in',
          time: '',
          label: ''
        };
      }
      
      // Use priority order for next reservation check-in time
      if (nextReservation.override_check_in) {
        // Extract time from ISO string using the same pattern as other components
        try {
          const isoString = nextReservation.override_check_in;
          const matches = isoString.match(/T(\d{2}):(\d{2}):(\d{2})/);
          
          if (matches && matches.length >= 3) {
            // Extract hours and minutes directly from ISO string
            const hours = matches[1];
            const minutes = matches[2];
            const timeResult = `${hours}:${minutes}`;
            return {
              date: formatDateForDisplay(nextReservation.check_in),
              time: convert24HourTo12Hour(timeResult),
              label: '(Next check-in - override)'
            };
          } else {
            // Fallback to formatTimeOnly if regex fails
            const timeResult = formatTimeOnly(nextReservation.override_check_in);
            return {
              date: formatDateForDisplay(nextReservation.check_in),
              time: timeResult,
              label: '(Next check-in - override)'
            };
          }
        } catch (error) {
          return {
            date: formatDateForDisplay(nextReservation.check_in),
            time: formatTimeOnly(nextReservation.override_check_in),
            label: '(Next check-in - override)'
          };
        }
              } else if (nextReservation.check_in_time) {
          return {
            date: formatDateForDisplay(nextReservation.check_in),
            time: convert24HourTo12Hour(nextReservation.check_in_time),
            label: '(Next check-in)'
          };
              } else if (nextReservation.property?.checkInTime) {
          return {
            date: formatDateForDisplay(nextReservation.check_in),
            time: convert24HourTo12Hour(nextReservation.property.checkInTime),
            label: '(Next check-in)'
          };
              } else {
          return {
            date: formatDateForDisplay(nextReservation.check_in),
            time: formatTimeOnly(nextReservation.check_in),
            label: '(Next check-in)'
          };
      }
    }
  };

  const startTimeInfo = getStartTime();
  const dueTimeInfo = getDueTime();

  return (
    <div className="bg-white shadow rounded-lg mb-3 lgmb-6">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-base lg:text-xl leading-6 font-medium text-gray-900 mb-4 flex items-center">
          <span>Service Type: </span>
          <span 
            className={`ml-2 px-3 py-1 rounded-md text-base lg:text-xl font-bold ${
              job?.serviceType?.color 
                ? `bg-opacity-15 text-${job.serviceType.color.replace('#', '')}`
                : 'bg-blue-100 text-blue-800'
            }`}
            style={{
              backgroundColor: job?.serviceType?.color ? `${job.serviceType.color}20` : '',
              color: job?.serviceType?.color || '',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}
          >
            {getServiceTypeDisplayName(job)}
          </span>
        </h3>
        
        {/* First row: Start Date, Start Time, and Assigned To in 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Start Date */}
          <div className="bg-gray-50 rounded-lg p-2 lg:p-4 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Start Date</p>
                <p className="text-base font-semibold text-gray-900">
                  {formatDateForDisplay(job.scheduledDate)}
                </p>
              </div>
            </div>
          </div>
          
          {/* Start Time */}
          <div className="bg-gray-50 rounded-lg p-2 lg:p-4 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Start Time</p>
                <p className="text-base font-semibold text-gray-900">
                  {startTimeInfo.time}
                  <span className="text-xs text-gray-500 ml-2">{startTimeInfo.label}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Assigned To */}
          <div className="bg-gray-50 rounded-lg p-2 lg:p-4 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {job.assignedTeamId && job.assignedTeam ? (
                  // Team assignment - show team icon
                  <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                ) : job.assignedTo ? (
                  <div className="h-8 w-8 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                    {job.assignedTo.avatarUrl ? (
                      <SafeAvatar
                        src={job.assignedTo.avatarUrl}
                        alt={job.assignedTo.name || job.assignedTo.email}
                        className="h-8 w-8 rounded-full"
                        size={32}
                        lazy={false}
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                        {(job.assignedTo.name || job.assignedTo.email || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                ) : (
                  <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Assigned to</p>
                <p className="text-base font-semibold text-gray-900">
                  {job.assignedTeamId && job.assignedTeam ? (
                    <span className="text-blue-700">
                      Team: {job.assignedTeam.name}
                    </span>
                  ) : job.assignedTo ? (
                    <span className="text-green-700">
                      {job.assignedTo.name || job.assignedTo.email}
                    </span>
                  ) : (
                    <span className="text-yellow-600 italic">
                      Unassigned
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Second row: Due, Reservation, and Payout in 2 or 3 columns depending on payout presence */}
        <div className={`grid grid-cols-1 md:grid-cols-${calculateGridCols()} gap-4`}>
          {/* Due By */}
          <div className="bg-gray-50 rounded-lg p-2 lg:p-4 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Due</p>
                <p className="text-base font-semibold text-gray-900">
                  {dueTimeInfo.date} {dueTimeInfo.time}
                  <span className="text-xs text-gray-500 ml-2">{dueTimeInfo.label}</span>
                </p>
              </div>
            </div>
          </div>
          
          {/* Reservation Type */}
          <div className="bg-gray-50 rounded-lg p-2 lg:p-4 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Reservation</p>
                {renderReservationBadgesContainer(job, "flex flex-wrap gap-2 mt-1", flagTemplates)}
              </div>
            </div>
          </div>
          
          {/* Payout Information - Only show if not public view AND payout is available */}
          {!isPublicView && canShowPayout && (
            <div className="bg-gray-50 rounded-lg p-2 lg:p-4 shadow-sm">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Payout</p>
                  <p className="text-base font-semibold text-gray-900">
                    {getPayoutValue()}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 